/**
 * Hook for managing publications filtering state
 * Similar to useApplicationsFilter from @uicwebmaster/applications
 */

'use client';

import { useMemo, useState, useTransition, useEffect } from 'react';
import type { Publication } from '../types/publication';

interface ThemeNode {
  id: string;
  name: string;
  color?: string | null;
  level?: number;
  children?: ThemeNode[];
}

interface Theme {
  id: string;
  name: string;
  color?: string | null;
  type: string;
  parentId: string;
  imageUrl?: string;
}

export interface UsePublicationsFilterOptions {
  publications: Publication[];
  pageSize?: number;
  themes?: Theme[];
}

/**
 * Build theme tree from flat themes array
 */
function buildThemeTree(themes: Theme[]): ThemeNode[] {
  const themeMap = new Map<string, ThemeNode>();
  const rootThemes: ThemeNode[] = [];

  // First pass: create all nodes
  themes.forEach(theme => {
    themeMap.set(theme.id, {
      id: theme.id,
      name: theme.name,
      color: theme.color,
      level: 0,
      children: [],
    });
  });

  // Second pass: build tree structure
  themes.forEach(theme => {
    const node = themeMap.get(theme.id);
    if (!node) return;

    if (theme.parentId && theme.parentId !== '0') {
      const parent = themeMap.get(theme.parentId);
      if (parent) {
        parent.children = parent.children || [];
        parent.children.push(node);
        node.level = (parent.level || 0) + 1;
      } else {
        rootThemes.push(node);
      }
    } else {
      rootThemes.push(node);
    }
  });

  return rootThemes;
}

/**
 * Hook for filtering and paginating publications
 */
export function usePublicationsFilter({
  publications,
  pageSize = 12,
  themes = [],
}: UsePublicationsFilterOptions) {
  const safePageSize = Math.max(1, pageSize);

  // Filter state
  const [query, setQuery] = useState('');
  const [selectedThemes, setSelectedThemes] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);

  // Use transition for non-urgent filter updates
  const [isPending, startTransition] = useTransition();

  const normalizedQuery = query.trim().toLowerCase();

  // Build theme tree from flat themes array
  const themesTree = useMemo(() => {
    if (!themes || themes.length === 0) return [];
    return buildThemeTree(themes);
  }, [themes]);

  // Extract themes from publications and build hierarchical tree
  const filteredThemes = useMemo(() => {
    const themeIdsInPublications = new Set<string>();

    publications.forEach(pub => {
      const pubThemes = pub.theme ?? [];
      pubThemes.forEach(theme => {
        if (theme?.id) {
          themeIdsInPublications.add(theme.id);
        }
      });
    });

    // If themesTree is available, build filtered tree
    if (themesTree && themesTree.length > 0) {
      const findAncestors = (
        nodes: ThemeNode[],
        targetId: string,
        currentPath: string[] = []
      ): Set<string> => {
        for (const node of nodes) {
          const newPath = [...currentPath, node.id];

          if (node.id === targetId) {
            return new Set(currentPath);
          }

          if (node.children && node.children.length > 0) {
            const found = findAncestors(node.children, targetId, newPath);
            if (found.size > 0) {
              return found;
            }
          }
        }
        return new Set<string>();
      };

      const allRelevantIds = new Set<string>(themeIdsInPublications);

      themeIdsInPublications.forEach(themeId => {
        const ancestors = findAncestors(themesTree, themeId);
        ancestors.forEach(ancestorId => allRelevantIds.add(ancestorId));
      });

      const filterTree = (nodes: ThemeNode[], parentColor?: string | null): ThemeNode[] => {
        const result: ThemeNode[] = [];

        for (const node of nodes) {
          if (allRelevantIds.has(node.id)) {
            const nodeColor =
              node.color && node.color.trim() !== ''
                ? node.color
                : parentColor && parentColor.trim() !== ''
                  ? parentColor
                  : undefined;

            const filteredChildren = node.children ? filterTree(node.children, nodeColor) : [];

            result.push({
              ...node,
              color: nodeColor,
              children: filteredChildren,
            });
          }
        }

        return result;
      };

      return filterTree(themesTree);
    }

    // Fallback: flat list
    const themeMap = new Map<string, { id: string; name: string; color?: string | null }>();

    publications.forEach(pub => {
      const pubThemes = pub.theme ?? [];
      pubThemes.forEach(theme => {
        if (theme?.id && theme?.name && !themeMap.has(theme.id)) {
          themeMap.set(theme.id, {
            id: theme.id,
            name: theme.name,
            color: theme.color,
          });
        }
      });
    });

    return Array.from(themeMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [publications, themesTree]);

  // Filter publications
  const filteredPublications = useMemo(() => {
    return publications.filter(pub => {
      // Theme filter
      if (selectedThemes.length > 0) {
        const pubThemeIds = pub.theme?.map(t => t?.id).filter(Boolean) || [];
        const hasSelectedTheme = selectedThemes.some(themeId => pubThemeIds.includes(themeId));
        if (!hasSelectedTheme) return false;
      }

      // Search query
      if (!normalizedQuery) return true;

      // Search in title
      if (pub.title?.toLowerCase().includes(normalizedQuery)) {
        return true;
      }

      // Search in pubRef
      if (pub.pubRef?.toLowerCase().includes(normalizedQuery)) {
        return true;
      }

      return false;
    });
  }, [publications, normalizedQuery, selectedThemes]);

  const totalPages = Math.max(1, Math.ceil(filteredPublications.length / safePageSize));

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [normalizedQuery, selectedThemes, safePageSize, publications]);

  // Ensure current page doesn&apos;t exceed total pages
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // Paginated publications
  const { visiblePublications, startIndex, endIndex } = useMemo(() => {
    const start = (currentPage - 1) * safePageSize;
    const end = Math.min(start + safePageSize, filteredPublications.length);
    return {
      visiblePublications: filteredPublications.slice(start, end),
      startIndex: start,
      endIndex: end,
    };
  }, [filteredPublications, currentPage, safePageSize]);

  // Actions - wrapped with startTransition
  const toggleTheme = (themeId: string) => {
    startTransition(() => {
      setSelectedThemes(prev =>
        prev.includes(themeId) ? prev.filter(id => id !== themeId) : [...prev, themeId]
      );
    });
  };

  const clearThemeSelection = () => {
    startTransition(() => {
      setSelectedThemes([]);
    });
  };

  const handlePageChange = (page: number) => {
    startTransition(() => {
      setCurrentPage(page);
    });
  };

  const clearAllFilters = () => {
    startTransition(() => {
      setQuery('');
      setSelectedThemes([]);
      setCurrentPage(1);
    });
  };

  const handleQueryChange = (newQuery: string) => {
    startTransition(() => {
      setQuery(newQuery);
    });
  };

  const totalResults = filteredPublications.length;
  const hasResults =
    totalResults > 0 || normalizedQuery !== '' || selectedThemes.length > 0;
  const hasActiveFilters = normalizedQuery !== '' || selectedThemes.length > 0;

  return {
    // State
    query,
    selectedThemes,
    currentPage,

    // Derived data
    themes: filteredThemes,
    visiblePublications,
    totalResults,
    hasResults,
    hasActiveFilters,
    totalPages,
    startIndex,
    endIndex,
    normalizedQuery,
    isPending,

    // Actions
    setQuery: handleQueryChange,
    toggleTheme,
    clearThemeSelection,
    handlePageChange,
    clearAllFilters,
  };
}

